<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { calculateMetricValue, calculateDeltaValue, DeltaType, MetricType, Type, getLabel } from '../utility';
import { useScreen } from '@Shared/utils/useScreen';
import { roundAwayFromZero } from '@Shared/utils/util';
import GreyDownArrowSvg from '@Shared/components/visuals/visualAssets/GreyDownArrow.svg?component';
import GreyUpArrowSvg from '@Shared/components/visuals/visualAssets/GreyUpArrow.svg?component';
import GreyLevelArrowSvg from '@Shared/components/visuals/visualAssets/GreyLevelArrow.svg?component';
import EmptyStateIcon from '@Shared/components/visuals/visualAssets/EmptyState_Icon.svg?component';
import Gauge from './Gauge.vue';

const props = defineProps<{
  showGauge: boolean;
  program: {
    title: string;
    count: number;
    metricType: MetricType;
    metricValue: MeasureResult;
    metricPercentages: {
      negative: number;
      neutral: number;
      positive: number;
      negativeCount: number;
      neutralCount: number;
      positiveCount: number;
    };
  };
}>();

const { program } = toRefs(props);
const screen = useScreen();

const getDeltaSvg = (measureResult: MeasureResult) => {
  const deltaValue = calculateDeltaValue(measureResult, DeltaType.PointDelta);

  if (deltaValue == null) return;
  if (deltaValue > 0) return GreyUpArrowSvg;
  if (deltaValue < 0) return GreyDownArrowSvg;
  return GreyLevelArrowSvg;
};

const getWidthForBar = (percentage: number) => {
  // the total width of the bar is dependent on whether the device size, if too small it will use a smaller full size
  let totalWidth;
  if (screen.value.width <= 450) {
    totalWidth = 250;
  } else {
    totalWidth = 350;
  }
  return (totalWidth / 100) * percentage;
};

const title = computed(() => {
  let title = program.value.title;
  if (props.showGauge) {
    title += program.value.metricType == MetricType.Nps ? ' NPS' : ' CSAT';
  }
  return title;
});

interface MetricPercentage {
  labelType: Type;
  displayLabel: string;
  percentage: number;
  count: number;
}

// Order data from positive -> neutral -> negative -> null to render
const metricPercentagesData = computed(() => {
  const data: MetricPercentage[] = [];
  const positiveCount = program.value.metricPercentages.positiveCount;
  const neutralCount = program.value.metricPercentages.neutralCount;
  const negativeCount = program.value.metricPercentages.negativeCount;
  const positivePercentage = Number(program.value.metricPercentages.positive);
  const neutralPercentage = Number(program.value.metricPercentages.neutral);
  const negativePercentage = Number(program.value.metricPercentages.negative);
  if (positiveCount > 0) {
    data.push({
      labelType: Type.Positive,
      displayLabel: getLabel(program.value.metricType)[Type.Positive],
      percentage: positivePercentage,
      count: positiveCount,
    });
  }
  if (neutralCount > 0) {
    data.push({
      labelType: Type.Neutral,
      displayLabel: getLabel(program.value.metricType)[Type.Neutral],
      percentage: neutralPercentage,
      count: neutralCount,
    });
  }
  if (negativeCount > 0) {
    data.push({
      labelType: Type.Negative,
      displayLabel: getLabel(program.value.metricType)[Type.Negative],
      percentage: negativePercentage,
      count: negativeCount,
    });
  }
  const nullCount = program.value.count - (positiveCount + neutralCount + negativeCount);
  if (nullCount > 0) {
    data.push({
      labelType: Type.Null,
      displayLabel: getLabel(program.value.metricType)[Type.Null],
      percentage: 100 - (positivePercentage + neutralPercentage + negativePercentage),
      count: nullCount,
    });
  }
  return data;
});
</script>

<template>
  <div v-if="program.count > 0" class="flex flex-col mt-4 border-solid border-0 border-[#BDBDBD]">
    <div class="lg:font-500 lg:text-3 xs:font-400 xs:text-4">
      {{ title }}
    </div>
    <div class="pb-6 mt-2 inline-block flex lg:flex-row lg:items-center xs:items-start xs:flex-col">
      <Gauge
        v-if="showGauge"
        class="lg:h-[80px] xs:max-h-[100px] lg:max-h-auto lg:mr-5 xs:mb-5 lg:mb-0"
        :delta="calculateDeltaValue(program.metricValue, DeltaType.PointDelta)"
        :value="program.metricValue?.currentValue"
        :metricType="program.metricType"
      ></Gauge>
      <div class="inline-block lg:w-[60%] xs:w-[100%] lg:flex lg:flex-row xl:inline-block">
        <div class="font-700 color-white lg:text-3 xs:text-5 inline-block flex flex-row">
          <div v-for="(data, i) in metricPercentagesData" :key="data.labelType">
            <div
              class="lg:h-6 text-center inline-block border-solid border-0 border-[#FFFFFF] xs:h-8 flex flex-col items-center justify-center relative group"
              :style="{
                width: getWidthForBar(data.percentage) + 'px',
              }"
              :class="{
                'rounded-l-3': i === 0,
                'rounded-r-3': i === metricPercentagesData.length - 1,
                'border-l-2': i - 1 > -1,
                'border-r-2': i + 1 < metricPercentagesData.length,
                'bg-[#0C8A65]': data.labelType === Type.Positive,
                'bg-[#7F7466]': data.labelType === Type.Neutral,
                'bg-[#D4002B]': data.labelType === Type.Negative,
                'bg-[#C5CDDC]': data.labelType === Type.Null,
              }"
            >
              <!-- if the bar width is too small (less than 10%) than we won't show the number and just want an empty bar,
                however, just adding a space resulted in the bar drawing in the wrong spot. That's why we use &nbsp -->
              <div class="xs:text-4 lg:text-3">
                {{ data.percentage >= 10 ? roundAwayFromZero(data.percentage) + '%' : '&nbsp;' }}
              </div>
              <div
                v-if="data.count > 0"
                class="absolute md:group-hover:visible invisible bottom-8 text-black bg-[#cfd6e2] rounded color-[#333333] font-400 pt-1 pb-1 pr-2 pl-2 text-nowrap"
              >
                {{ data.displayLabel }}:
                {{ data.count }}
              </div>
            </div>
          </div>
        </div>
        <div class="font-500 lg:text-3 xs:mt-4 lg:mt-1 xl:mt-0 xs:text-4 inline-block align-middle lg:ml-2 xl:ml-0">
          <div class="inline-block">{{ program.count }} Responses</div>
          <div v-if="!showGauge" class="lg:ml-4 xs:ml-6 inline-block">
            {{ program.metricType == MetricType.Nps ? 'NPS' : 'CSAT' }}
          </div>
          <div v-if="!showGauge" class="ml-1 inline-block">
            {{ calculateMetricValue(program.metricValue.currentValue, program.metricType) }}
          </div>
          <component
            v-if="!showGauge"
            class="lg:ml-1 lg:w-[14px] lg:h-[14px] pt-1 xs:ml-6 xs:w-[18px] xs:h-[18px] box-border"
            :is="getDeltaSvg(program.metricValue)"
          ></component>
          <div
            class="ml-1 inline-block"
            v-if="!showGauge && calculateDeltaValue(program.metricValue, DeltaType.PointDelta)"
          >
            {{ roundAwayFromZero(calculateDeltaValue(program.metricValue, DeltaType.PointDelta)! * 100) }}
          </div>
          <div
            v-else-if="!showGauge && !calculateDeltaValue(program.metricValue, DeltaType.PointDelta)"
            class="ml-2 inline-block"
          >
            -
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Empty State -->
  <div v-else class="flex flex-col mt-4 border-solid border-0 border-[#BDBDBD] text-12px">
    <div class="lg:font-500 lg:text-3 xs:font-400 xs:text-4">
      {{ program.title }}
    </div>
    <div class="flex items-center gap-[5px] mt-[19px] mb-6">
      <EmptyStateIcon /> No data to display for this program.
    </div>
  </div>
</template>
